import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import { useMaterialUIController } from "context";
import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { getDevice, putDevice } from "../../../api/devices";

import { getOrder } from "../../../api/orders";

import CostingInfos from "./CostingInfos";
import RealCostingInfos from "./RealCostingInfos";
import PlannedInfos from "./PlannedInfos";

import PropTypes from "prop-types";

import { setCurrentDevices } from "../../../redux/reducers/devicesReducer";

import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";
import { useDispatch, useSelector } from "react-redux";

export default function EditCosting({ device = null, edit = false }) {
  const [open, setOpen] = useState(false);

  const [deviceId, setDeviceId] = useState("");
  const [purchases, setPurchases] = useState(null);
  const [hoursSold, setHoursSold] = useState(null);
  const [price, setPrice] = useState(null);
  const [realPurchases, setRealPurchases] = useState(null);
  const [hoursPast, setHoursPast] = useState(null);
  const [hoursPlanned, setHoursPlanned] = useState(null);

  const currentOrder = useSelector((state) => state.orders.currentOrder);

  const dispatch = useDispatch();

  useEffect(() => {
    if (edit && device) {
      setDeviceId(device.device_id);
      setPurchases(device.purchases);
      setHoursSold(device.hours_sold);
      setPrice(device.price);
      setRealPurchases(device.real_purchases);
      setHoursPast(device.hours_past);
      setHoursPlanned(device.hours_planned);
    }
  }, [edit, device]);

  async function updateDeviceAPI() {
    const device = {
      purchases: purchases,
      hours_sold: hoursSold,
      price: price,
      real_purchases: realPurchases,
      hours_past: hoursPast,
      hours_planned: hoursPlanned,
    };
    await putDevice(device, deviceId);
  }

  async function fetchOrder() {
    const response = await getOrder(currentOrder.order_id);
    dispatch(setCurrentOrder(response.data));
  }

  async function fetchDevice(deviceId) {
    const response = await getDevice(deviceId);
    return await response.data;
  }

  const fetchDevices = async () => {
    if (currentOrder) {
      if (currentOrder.devices_ids) {
        const devices = [];
        const devices_id = currentOrder.devices_ids.split(",");
        for (let i = 0; i < devices_id.length; i++) {
          devices.push(await fetchDevice(devices_id[i]));
        }

        // Sort the devices array based on the device identifier
        devices.sort((a, b) => {
          // Extract the base device identifier (ignoring anything after a hyphen)
          const baseIdA = a.device_number.split(" -")[0];
          const baseIdB = b.device_number.split(" -")[0];

          // Compare the base identifiers
          if (baseIdA < baseIdB) return -1;
          if (baseIdA > baseIdB) return 1;

          // If base identifiers are equal, compare the full device_id to maintain relative order
          return a.device_number.localeCompare(b.device_number);
        });

        dispatch(setCurrentDevices(devices));
      }
    }
  };

  async function cancel() {
    fetchOrder();
    setStep(0);
    setOpen(false);
  }

  async function submit() {
    if (edit) {
      await updateDeviceAPI();
      await fetchOrder();
      await fetchDevices();
    }
    setStep(0);
    setOpen(false);
  }

  const [step, setStep] = useState(0);
  const steps = ["Prévisionnel", "Planifié", "Réel"];
  const isLastStep = step === steps.length - 1;

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <CostingInfos
            deviceId={deviceId}
            purchases={purchases}
            setPurchases={setPurchases}
            hoursSold={hoursSold}
            setHoursSold={setHoursSold}
          />
        );
      case 1:
        return (
          <PlannedInfos
            deviceId={deviceId}
            hoursPlanned={hoursPlanned}
            setHoursPlanned={setHoursPlanned}
          />
        );
      case 2:
        return (
          <RealCostingInfos
            deviceId={deviceId}
            price={price}
            setPrice={setPrice}
            realPurchases={realPurchases}
            setRealPurchases={setRealPurchases}
            hoursPast={hoursPast}
            setHoursPast={setHoursPast}
          />
        );
      default:
        return null;
    }
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <MDButton
          variant="contained"
          style={{ height: 53.13 }}
          color="success"
          onClick={() => setOpen(true)}
        >
          Ajouter une commande
        </MDButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={edit ? -12 : -5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={darkMode ? "inherit" : "white"}
                      >
                        Modification du chiffrage
                      </MDTypography>
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Veuillez rentrer les informations relatives au chiffrage
                    </MDTypography>
                  </MDBox>
                  <Stepper
                    sx={{ boxShadow: "none", backgroundImage: "none" }}
                    activeStep={step}
                    alternativeLabel
                  >
                    {steps.map((label) => (
                      <Step
                        sx={{
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                            {
                              color: darkMode && "#344767 !important",
                            },
                          "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                            {
                              color: darkMode && "#344767 !important",
                            },
                        }}
                        key={label}
                      >
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-label.Mui-active": {
                              color: darkMode && "#344767 !important",
                            },
                            "& .MuiStepLabel-label.Mui-completed": {
                              color: darkMode && "#344767 !important",
                            },
                            "& .MuiStepIcon-root.Mui-active": {
                              background: darkMode && "#344767",
                              fill: darkMode && "#344767",
                              stroke: darkMode && "#344767",
                              borderColor: darkMode && "#344767",
                              boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                            },
                            "& .MuiStepIcon-root.Mui-completed": {
                              background: darkMode && "#344767",
                              fill: darkMode && "#344767",
                              stroke: darkMode && "#344767",
                              borderColor: darkMode && "#344767",
                              boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                            },
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {getStepContent(step)}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {step === 0 ? (
                        <MDBox />
                      ) : (
                        <MDButton variant="gradient" color="warning" onClick={previous}>
                          Précédent
                        </MDButton>
                      )}
                      <MDBox textAlign="right">
                        <Grid container spacing={1.5}>
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              variant="gradient"
                              color="success"
                              onClick={!isLastStep ? next : submit}
                            >
                              {isLastStep ? "Envoyer" : "Suivant"}
                            </MDButton>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

EditCosting.propTypes = {
  edit: PropTypes.bool.isRequired,
};
